import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import LoadingButton from '@mui/lab/LoadingButton';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import EastRoundedIcon from '@mui/icons-material/EastRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import {
  Box,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import Busy from '../../components/Busy';
import { useAPIAuthenticatedService } from '../../helpers/APICallsAuthenticated';
import {
  CacheKeys,
  ReferralConversationEventType,
  ReferralConversationMessage,
  ReferralConversationParticipant,
  ReferralConversationResponse,
  ReferralConversationViewRole,
  ReferralWithScope_UserWithoutId,
  User_WithoutId,
} from '../../helpers/DataStructures';
import { syncAndroidReferralBadgeCountFromApi } from '../../helpers/referralBadgeSync';
import ReferralConversationCounterpartyHeader from './ReferralConversationCounterpartyHeader';
import ReferralInStatusActions from './ReferralInStatusActions';

interface ReferralConversationDetailProps {
  referral: ReferralWithScope_UserWithoutId;
  showCounterpartyHeader?: boolean;
  viewAsRole?: ReferralConversationViewRole;
  viewerUserIdOverride?: string | null;
  readOnly?: boolean;
  loadConversation?: (referralId: number) => Promise<ReferralConversationResponse>;
  conversationQueryKey?: readonly unknown[];
  suppressParticipantRefresh?: boolean;
}

type ConversationEventTone = 'neutral' | 'positive' | 'negative';

const EVENT_TYPE_LABELS: Record<Exclude<ReferralConversationEventType, null>, string> = {
  referral_created: 'Referral created',
  referral_going_ahead: 'Job going ahead',
  referral_succeeded: 'Job completed',
  referral_failed: 'Referral unsuccessful',
  referral_already_customer: 'Already a customer',
  referral_payout_collected: 'Referral fee collected',
  referral_payout_sent: 'Referral fee paid out',
};

const formatMessageTimestamp = (value: string) =>
  new Intl.DateTimeFormat('en-GB', {
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(value));

const formatDayLabel = (value: string) =>
  new Intl.DateTimeFormat('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  }).format(new Date(value));

const isSameDay = (left: string, right: string) =>
  new Date(left).toDateString() === new Date(right).toDateString();

const isConversationEvent = (message: ReferralConversationMessage) =>
  message.senderType !== 'user' || message.eventType !== null;

const isCreatedConversationEvent = (message: ReferralConversationMessage) =>
  message.eventType === 'referral_created';

const isMessageStyleStatusEvent = (message: ReferralConversationMessage) =>
  message.senderType === 'user' &&
  message.eventType !== null &&
  message.eventType !== 'referral_created';

const isCenteredConversationEvent = (message: ReferralConversationMessage) =>
  isConversationEvent(message) &&
  !isMessageStyleStatusEvent(message) &&
  !isCreatedConversationEvent(message);

const getEventTitle = (
  message: ReferralConversationMessage,
  isOfferOwner: boolean
) => {
  if (message.eventType) {
    if (message.eventType === 'referral_created') {
      return isOfferOwner ? 'Referral received' : 'Referral sent';
    }

    return EVENT_TYPE_LABELS[message.eventType];
  }

  if (message.senderType === 'admin') {
    return message.senderLabel ?? 'Admin update';
  }

  if (message.senderType === 'system') {
    return message.senderLabel ?? 'System update';
  }

  return message.senderLabel ?? 'Update';
};

const isStrongEventTitle = (message: ReferralConversationMessage) =>
  message.eventType === 'referral_created';

const getEventTone = (message: ReferralConversationMessage): ConversationEventTone => {
  switch (message.eventType) {
    case 'referral_created':
    case 'referral_going_ahead':
    case 'referral_succeeded':
    case 'referral_payout_collected':
    case 'referral_payout_sent':
      return 'positive';
    case 'referral_failed':
    case 'referral_already_customer':
      return 'negative';
    default:
      return 'neutral';
  }
};

const getEventToneStyles = (tone: ConversationEventTone) => {
  if (tone === 'positive') {
    return {
      color: 'rgba(152, 255, 168, 0.92)',
      backgroundColor: 'rgba(8,242,40,0.09)',
      borderColor: 'rgba(8,242,40,0.2)',
    };
  }

  if (tone === 'negative') {
    return {
      color: 'rgba(255, 190, 190, 0.92)',
      backgroundColor: 'rgba(255,0,0,0.09)',
      borderColor: 'rgba(255,0,0,0.2)',
    };
  }

  return {
    color: 'text.primary',
    backgroundColor: 'rgba(255,255,255,0.08)',
    borderColor: 'rgba(255,255,255,0.14)',
  };
};

const getCreatedEventMetaRows = (
  referral: ReferralWithScope_UserWithoutId,
  isOfferOwner: boolean
) => {
  const rows: Array<{ label?: string; value: string }> = [
    {
      label: isOfferOwner ? 'From' : 'To',
      value: isOfferOwner
        ? `${referral.user.givenName} ${referral.user.familyName}`
        : referral.offer.user.companyName
        ? referral.offer.user.companyName
        : `${referral.offer.user.givenName} ${referral.offer.user.familyName}`,
    },
    {
      label: 'Customer',
      value: referral.theReferralName,
    },
    {
      label: 'Contact',
      value: referral.theReferralContact,
    },
  ];

  if (referral.theReferralDetails) {
    rows.push({
      // REQ-014: keep the referral description readable without a redundant "Details" prefix.
      value: referral.theReferralDetails,
    });
  }

  // REQ-014: both participants should see the same core referral context in the created-event row.
  rows.push({
    label: 'Offer',
    value: `${referral.offer.location} - \u00A3${referral.offer.offeringFixedCashAmount}`,
  });

  return rows;
};

const getSuccessfulEventFeeLabel = (
  message: ReferralConversationMessage,
  referral: ReferralWithScope_UserWithoutId
) => {
  if (message.eventType !== 'referral_succeeded' || referral.offer.offeringFixedCashAmount <= 0) {
    return null;
  }

  return `\u00A3${referral.offer.offeringFixedCashAmount}`;
};

const isOwnConversationRow = (
  message: ReferralConversationMessage,
  viewerUserId: string | null,
  isOfferOwner: boolean
) => {
  if (message.eventType === 'referral_created') {
    return !isOfferOwner;
  }

  return (
    message.senderType === 'user' &&
    viewerUserId !== null &&
    message.senderUserId === viewerUserId
  );
};

const getConversationSenderLabel = (
  message: ReferralConversationMessage,
  isOwnMessage: boolean
) => {
  if (isOwnMessage) {
    return null;
  }

  if (message.eventType !== null) {
    return null;
  }

  return message.senderLabel;
};

type ConversationDeliveryState = 'sent' | 'delivered';

const getOwnConversationDeliveryState = (
  message: ReferralConversationMessage,
  isOwnMessage: boolean,
  participants: ReferralConversationParticipant[],
  viewerUserId: string | null,
  isCenteredEvent: boolean
): ConversationDeliveryState | null => {
  if (
    viewerUserId === null ||
    !isOwnMessage ||
    isCenteredEvent
  ) {
    return null;
  }

  const counterpartyParticipants = participants.filter(
    (participant) => participant.userId !== viewerUserId && participant.participantRole !== 'admin'
  );

  if (counterpartyParticipants.length === 0) {
    return null;
  }

  // REQ-013: own in-flow conversation rows should show whether every counterparty has read past that item.
  const hasBeenRead = counterpartyParticipants.every(
    (participant) =>
      participant.lastReadMessageId !== null && participant.lastReadMessageId >= message.id
  );

  return hasBeenRead ? 'delivered' : 'sent';
};

const getViewerUserIdFromConversationRole = (
  participants: ReferralConversationParticipant[] | undefined,
  viewAsRole: ReferralConversationViewRole | undefined
) => {
  if (!participants || viewAsRole === undefined) {
    return null;
  }

  return (
    participants.find((participant) => participant.participantRole === viewAsRole)?.userId ?? null
  );
};

const getViewerUserIdFromReferralIsMe = (
  referral: ReferralWithScope_UserWithoutId,
  isOfferOwner: boolean
) => {
  const viewerUser = isOfferOwner ? referral.offer.user : referral.user;

  return viewerUser.isMe === true ? viewerUser.id ?? null : null;
};

const ReferralConversationDetail: React.FC<ReferralConversationDetailProps> = ({
  referral,
  showCounterpartyHeader = true,
  viewAsRole,
  viewerUserIdOverride,
  readOnly = false,
  loadConversation,
  conversationQueryKey,
  suppressParticipantRefresh = false,
}) => {
  const apiServiceInstance = useAPIAuthenticatedService();
  const queryClient = useQueryClient();
  const [draftMessage, setDraftMessage] = useState('');
  const [isStatusUpdating, setIsStatusUpdating] = useState(false);
  const composerRef = useRef<HTMLTextAreaElement | null>(null);
  // REQ-002: keep the current conversation view on the latest confirmed status immediately.
  const [displayStatus, setDisplayStatus] = useState(referral.status);
  const messageEndRef = useRef<HTMLDivElement | null>(null);
  // REQ-002: skip the next referral cache refresh after a status change so stale data cannot win.
  const skipConversationCacheRefreshRef = useRef(false);

  // REQ-027: allow admin read-only views to reuse the same conversation component from an explicit participant perspective.
  const isOfferOwner =
    viewAsRole !== undefined ? viewAsRole === 'offerer' : referral.offer.user.isMe === true;
  const resolvedConversationQueryKey =
    conversationQueryKey ?? [CacheKeys.REFERRAL_CONVERSATION, referral.id];
  const viewerUserIdFromReferral = getViewerUserIdFromReferralIsMe(referral, isOfferOwner);

  const cachedLoggedInUser = queryClient.getQueryData<User_WithoutId | null | undefined>([
    CacheKeys.LOGGED_IN_USER,
  ]);

  const { data: fetchedLoggedInUser } = useQuery(
    [CacheKeys.LOGGED_IN_USER],
    () => apiServiceInstance.getAuthenticatedUser(),
    {
      enabled:
        viewAsRole === undefined &&
        viewerUserIdFromReferral === null &&
        cachedLoggedInUser === undefined &&
        viewerUserIdOverride === undefined,
      staleTime: Infinity,
    }
  );

  const refreshReferralCaches = async () => {
    if (suppressParticipantRefresh) {
      return;
    }

    await Promise.all([
      queryClient.invalidateQueries([CacheKeys.REFERRAL_BY_ID, referral.id]),
      queryClient.invalidateQueries([CacheKeys.REFERRALS_TO_USER]),
      queryClient.invalidateQueries([CacheKeys.REFERRALS_FROM_USER]),
    ]);

    await syncAndroidReferralBadgeCountFromApi(apiServiceInstance);
  };

  const {
    data: conversationData,
    isLoading: isConversationLoading,
    isError: isConversationError,
  } = useQuery(
    resolvedConversationQueryKey,
    () => (loadConversation ? loadConversation(referral.id) : apiServiceInstance.getReferralConversation(referral.id)),
    {
      // REQ-001: changing referral ids should clear old conversation rows instead of keeping them.
      onSuccess: async () => {
        if (suppressParticipantRefresh) {
          return;
        }

        if (skipConversationCacheRefreshRef.current) {
          skipConversationCacheRefreshRef.current = false;
          await syncAndroidReferralBadgeCountFromApi(apiServiceInstance);
          return;
        }

        await refreshReferralCaches();
      },
    }
  );

  const sendMessageMutation = useMutation(
    (message: string) => apiServiceInstance.addReferralConversationMessage(referral.id, message),
    {
      onSuccess: async () => {
        setDraftMessage('');
        await queryClient.invalidateQueries(resolvedConversationQueryKey);
        await refreshReferralCaches();
      },
    }
  );

  useEffect(() => {
    if (!conversationData) {
      return;
    }

    messageEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [conversationData]);

  useEffect(() => {
    setDisplayStatus(referral.status);
  }, [referral.id, referral.status]);

  useEffect(() => {
    if (!composerRef.current) {
      return;
    }

    const composer = composerRef.current;
    composer.style.height = '0px';

    // REQ-006: mobile composer behaves like a single-row chat input that grows with content.
    const nextHeight = Math.min(Math.max(composer.scrollHeight, 48), 160);
    composer.style.height = `${nextHeight}px`;
    composer.style.overflowY = composer.scrollHeight > 160 ? 'auto' : 'hidden';
  }, [draftMessage]);

  // REQ-004: prefer the authenticated user id from cache so own messages render on the right immediately.
  const viewerUserIdFromRole = getViewerUserIdFromConversationRole(
    conversationData?.participants,
    viewAsRole
  );

  const viewerUserId =
    viewerUserIdOverride ??
    // REQ-041: admin read-only views may only receive participant ids from the conversation payload.
    viewerUserIdFromRole ??
    // REQ-042: normal conversation ownership should trust the referral participant marked as the viewer.
    viewerUserIdFromReferral ??
    (viewAsRole === undefined
      ? cachedLoggedInUser?.id ?? fetchedLoggedInUser?.id ?? null
      : null);
  const conversationExists = conversationData?.conversation.exists !== false;
  const composerDisabled =
    readOnly ||
    !conversationData ||
    isConversationError ||
    !conversationExists ||
    sendMessageMutation.isLoading ||
    // REQ-003: once a draft is being used as a status note, lock the composer until that update finishes.
    isStatusUpdating;
  const trimmedDraft = draftMessage.trim();

  const messageRows = useMemo(() => {
    if (!conversationData) {
      return [];
    }

    return conversationData.messages.map((message, index) => ({
      message,
      showDayDivider:
        index === 0 ||
        !isSameDay(conversationData.messages[index - 1].created, message.created),
    }));
  }, [conversationData]);

  const handleSendMessage = () => {
    if (!trimmedDraft || composerDisabled) {
      return;
    }

    // REQ-030: message send failures must stay in handled mutation state and remain visible.
    sendMessageMutation.reset();
    sendMessageMutation.mutate(trimmedDraft);
  };

  const handleStatusUpdated = async () => {
    setDraftMessage('');
    skipConversationCacheRefreshRef.current = true;
    await queryClient.invalidateQueries(resolvedConversationQueryKey);

    if (!suppressParticipantRefresh) {
      await syncAndroidReferralBadgeCountFromApi(apiServiceInstance);
    }
  };

  const sendMessageErrorMessage =
    sendMessageMutation.error instanceof Error && sendMessageMutation.error.message
      ? sendMessageMutation.error.message
      : 'Unable to send message. Please try again.';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
      }}
    >
      <Paper
        variant="outlined"
        data-testid="conversation-panel"
        sx={{
          // REQ-010: mobile keeps the conversation panel flush instead of showing card chrome.
          display: 'flex',
          flexDirection: 'column',
          minHeight: { xs: '60vh', md: '68vh' },
          overflow: 'hidden',
          backgroundColor: 'rgba(10,10,10,0.92)',
          border: { xs: 'none', sm: undefined },
          borderRadius: { xs: 0, sm: 1 },
          mx: { xs: -2, sm: 0 },
          mb: { xs: -2, sm: 0 },
        }}
      >
        {showCounterpartyHeader ? (
          <Box
            sx={{
              px: { xs: 1, sm: 2 },
              py: 1.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'rgba(255,255,255,0.03)',
            }}
          >
            <ReferralConversationCounterpartyHeader referral={referral} isOfferOwner={isOfferOwner} />
          </Box>
        ) : null}

        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            px: { xs: 1, sm: 2 },
            py: 2,
          }}
        >
          {isConversationLoading ? (
            <Busy message="Loading conversation..." />
          ) : isConversationError || !conversationData ? (
            <Typography>Unable to load the conversation.</Typography>
          ) : !conversationData.conversation.exists ? (
            <Box
              sx={{
                minHeight: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                px: 2,
              }}
            >
              <Stack spacing={1}>
                <Typography variant="h6">Conversation unavailable</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  This older referral does not have a conversation history available. New chat
                  messages are disabled for this referral.
                </Typography>
              </Stack>
            </Box>
          ) : conversationData.messages.length === 0 ? (
            <Box
              sx={{
                minHeight: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                px: 2,
              }}
            >
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                No conversation messages yet.
              </Typography>
            </Box>
          ) : (
            <Stack spacing={1.5}>
              {messageRows.map(({ message, showDayDivider }) => {
                const isOwnMessage = isOwnConversationRow(message, viewerUserId, isOfferOwner);
                const isStatusEvent = isMessageStyleStatusEvent(message);
                const isCreatedEvent = isCreatedConversationEvent(message);
                const isCenteredEvent = isCenteredConversationEvent(message);
                const eventTone = getEventTone(message);
                const eventToneStyles = getEventToneStyles(eventTone);
                const successfulEventFeeLabel = getSuccessfulEventFeeLabel(message, referral);
                const senderLabel = getConversationSenderLabel(message, isOwnMessage);
                const deliveryState = conversationData
                  ? getOwnConversationDeliveryState(
                      message,
                      isOwnMessage,
                      conversationData.participants,
                      viewerUserId,
                      isCenteredEvent
                    )
                  : null;

                return (
                  <React.Fragment key={message.id}>
                    {showDayDivider ? (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          my: 1,
                        }}
                      >
                        <Divider sx={{ flex: 1 }} />
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {formatDayLabel(message.created)}
                        </Typography>
                        <Divider sx={{ flex: 1 }} />
                      </Box>
                    ) : null}

                    {isCenteredEvent ? (
                      <Box display="flex" justifyContent="center">
                        <Paper
                          variant="outlined"
                          sx={{
                            maxWidth: 520,
                            px: 2,
                            py: 1.5,
                            backgroundColor: 'rgba(255,255,255,0.06)',
                            textAlign: 'center',
                          }}
                          data-testid={`conversation-event-${message.id}`}
                        >
                          <Stack spacing={1}>
                            <Box display="flex" justifyContent="center">
                              <Box
                                component="span"
                                data-event-tone={eventTone}
                                data-testid={`conversation-event-title-${message.id}`}
                                sx={{
                                  // REQ-015: event titles should carry positive/negative state with consistent pill styling.
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  px: 1,
                                  py: 0.3,
                                  borderRadius: '999px',
                                  border: '1px solid',
                                  fontSize: isStrongEventTitle(message) ? '0.9rem' : '0.8rem',
                                  lineHeight: 1.2,
                                  letterSpacing: '0.01em',
                                  // REQ-015: the initial sent/received status should read as the main thread-opening state.
                                  fontWeight: 600,
                                  ...eventToneStyles,
                                }}
                              >
                                {getEventTitle(message, isOfferOwner)}
                              </Box>
                            </Box>
                            {message.body && message.eventType !== 'referral_created' ? (
                              <Typography variant="body2">{message.body}</Typography>
                            ) : null}
                            {message.eventType === 'referral_created' ? (
                              <Stack
                                spacing={0.5}
                                sx={{
                                  mt: 0.75,
                                  pt: 0.75,
                                  borderTop: 1,
                                  borderColor: 'divider',
                                  textAlign: 'left',
                                }}
                              >
                                {getCreatedEventMetaRows(referral, isOfferOwner).map((row, index) => (
                                  <Typography
                                    key={`${row.label ?? 'value'}-${index}`}
                                    variant="body2"
                                    sx={{ wordBreak: 'break-word' }}
                                  >
                                    {row.label ? (
                                      <Box
                                        component="span"
                                        sx={{ color: 'text.secondary', fontWeight: 700, mr: 0.75 }}
                                      >
                                        {/* REQ-014: the created event carries the referral context once the summary card is removed. */}
                                        {row.label}:
                                      </Box>
                                    ) : null}
                                    {row.value}
                                  </Typography>
                                ))}
                              </Stack>
                            ) : null}
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              {formatMessageTimestamp(message.created)}
                            </Typography>
                          </Stack>
                        </Paper>
                      </Box>
                    ) : (
                      <Box
                        display="flex"
                        justifyContent={isOwnMessage ? 'flex-end' : 'flex-start'}
                      >
                        <Box
                          sx={(theme) => ({
                            maxWidth: { xs: '88%', sm: '72%' },
                            px: 1.5,
                            py: 1,
                            borderRadius: 3,
                            // REQ-016: own chat bubbles should stay visually distinct from green action controls.
                            backgroundColor: isOwnMessage
                              ? 'rgba(25, 118, 210, 0.22)'
                              : 'rgba(255,255,255,0.08)',
                            border: isOwnMessage
                              ? `1px solid rgba(25, 118, 210, 0.34)`
                              : '1px solid rgba(255,255,255,0.1)',
                            color: isOwnMessage ? theme.palette.text.primary : 'text.primary',
                          })}
                          data-testid={
                            isOwnMessage
                              ? `conversation-own-${message.id}`
                              : `conversation-other-${message.id}`
                          }
                        >
                          <Stack spacing={1}>
                            {!isOwnMessage && senderLabel ? (
                              <Typography
                                variant="caption"
                                sx={{ color: 'text.secondary', fontWeight: 700 }}
                              >
                                {senderLabel}
                              </Typography>
                            ) : null}
                            {isStatusEvent || isCreatedEvent ? (
                              <Box display="flex" justifyContent="center">
                                <Box
                                  component="span"
                                  data-event-tone={eventTone}
                                  data-testid={`conversation-event-title-${message.id}`}
                                  sx={{
                                    // REQ-017: event rows that live in the message flow should still keep a centered pill treatment.
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    px: 1,
                                    py: 0.3,
                                    borderRadius: '999px',
                                    border: '1px solid',
                                    fontWeight: 600,
                                    fontSize: isCreatedEvent ? '0.9rem' : '0.8rem',
                                    lineHeight: 1.2,
                                    letterSpacing: '0.01em',
                                    ...eventToneStyles,
                                  }}
                                >
                                  {getEventTitle(message, isOfferOwner)}
                                </Box>
                              </Box>
                            ) : null}
                            {message.body && !isCreatedEvent ? (
                              <Typography variant="body2">{message.body}</Typography>
                            ) : null}
                            {isCreatedEvent ? (
                              <Stack
                                spacing={0.5}
                                sx={{
                                  pt: 0.25,
                                  textAlign: 'left',
                                }}
                              >
                                {getCreatedEventMetaRows(referral, isOfferOwner).map((row, index) => (
                                  <Typography
                                    key={`${row.label ?? 'value'}-${index}`}
                                    variant="body2"
                                    sx={{ wordBreak: 'break-word' }}
                                  >
                                    {row.label ? (
                                      <Box
                                        component="span"
                                        sx={{ color: 'text.secondary', fontWeight: 700, mr: 0.75 }}
                                      >
                                        {/* REQ-014: the created event carries the referral context once the summary card is removed. */}
                                        {row.label}:
                                      </Box>
                                    ) : null}
                                    {row.value}
                                  </Typography>
                                ))}
                              </Stack>
                            ) : null}
                            <Box
                              sx={{
                                display: 'flex',
                                alignSelf: 'stretch',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                minHeight: '1rem',
                              }}
                            >
                              {deliveryState ? (
                                <Typography
                                  variant="caption"
                                  data-delivery-state={deliveryState}
                                  data-delivery-glyph={
                                    deliveryState === 'delivered' ? 'check' : 'arrow-right'
                                  }
                                  data-testid={`conversation-delivery-marker-${message.id}`}
                                  sx={{
                                    // REQ-013: own bubble delivery markers should stay discreet at the bottom-left.
                                    color: 'rgba(255,255,255,0.5)',
                                    fontSize: '0.72rem',
                                    lineHeight: 1,
                                    letterSpacing: 0,
                                    mr: 1,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                  }}
                                >
                                  {deliveryState === 'delivered' ? (
                                    <CheckRoundedIcon sx={{ fontSize: '0.85rem' }} />
                                  ) : (
                                    <EastRoundedIcon sx={{ fontSize: '0.8rem' }} />
                                  )}
                                </Typography>
                              ) : null}
                              <Typography
                                variant="caption"
                                sx={{
                                  opacity: 0.8,
                                }}
                              >
                                {formatMessageTimestamp(message.created)}
                              </Typography>
                            </Box>
                          </Stack>
                        </Box>
                      </Box>
                    )}
                    {successfulEventFeeLabel ? (
                      <Box display="flex" justifyContent="center" mt={0.75}>
                        <Paper
                          variant="outlined"
                          data-testid={`conversation-fee-award-${message.id}`}
                          sx={{
                            // REQ-022: successful referrals should add a standalone centered fee-award notice after the success message.
                            maxWidth: 320,
                            px: 2.5,
                            py: 1.5,
                            backgroundColor: 'transparent',
                            borderColor: 'transparent',
                            boxShadow: 'none',
                            textAlign: 'center',
                          }}
                        >
                          <Stack spacing={1}>
                            <Box
                              component="div"
                              data-testid={`conversation-fee-award-amount-${message.id}`}
                              sx={{
                                color: 'primary.main',
                                display: 'block',
                                fontSize: '3rem !important',
                                fontWeight: 800,
                                lineHeight: 0.92,
                                letterSpacing: '-0.04em',
                              }}
                            >
                              {successfulEventFeeLabel}
                            </Box>
                            <Box
                              component="div"
                              data-testid={`conversation-fee-award-label-${message.id}`}
                              sx={{
                                color: 'text.primary',
                                display: 'block',
                                fontSize: '1.4rem !important',
                                fontWeight: 700,
                                lineHeight: 1.18,
                                mt: 0.25,
                              }}
                            >
                              Awarded
                            </Box>
                          </Stack>
                        </Paper>
                      </Box>
                    ) : null}
                  </React.Fragment>
                );
              })}
              <div ref={messageEndRef} />
            </Stack>
          )}
        </Box>

        {!readOnly ? (
          <Box
            sx={{
              borderTop: 1,
              borderColor: 'divider',
              px: { xs: 1, sm: 2 },
              py: 2,
              backgroundColor: 'rgba(0,0,0,0.88)',
            }}
          >
            <Stack spacing={1.5}>
              {isOfferOwner ? (
                <Box>
                  <ReferralInStatusActions
                    disabled={sendMessageMutation.isLoading}
                    referral={{ ...referral, status: displayStatus }}
                    onUpdateStart={() => {
                      setIsStatusUpdating(true);
                    }}
                    onUpdateEnd={() => {
                      setIsStatusUpdating(false);
                    }}
                    // REQ-003: the current draft can be attached to a status change, so the composer is locked above.
                    statusMessage={conversationData && conversationExists ? draftMessage : undefined}
                    onUpdated={(updatedReferral) => {
                      setDisplayStatus(updatedReferral.status);
                      void handleStatusUpdated();
                    }}
                  />
                </Box>
              ) : null}

              {!conversationExists ? (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Messaging is disabled because this referral does not have a stored conversation
                  history.
                </Typography>
              ) : null}

              <Stack direction="row" spacing={1.5} alignItems="stretch">
                <Box
                  component="textarea"
                  ref={composerRef}
                  data-testid="conversation-composer"
                  disabled={composerDisabled}
                  placeholder="Write a message"
                  rows={1}
                  value={draftMessage}
                  onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
                    if (sendMessageMutation.isError) {
                      sendMessageMutation.reset();
                    }

                    setDraftMessage(event.target.value);
                  }}
                  sx={{
                    // REQ-006: keep the composer as a compact single row on mobile until more lines are needed.
                    width: '100%',
                    minHeight: 48,
                    maxHeight: 160,
                    resize: 'none',
                    borderRadius: { xs: 6, sm: 2 },
                    border: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: composerDisabled ? 'rgba(255,255,255,0.04)' : '#fff',
                    color: composerDisabled ? 'text.disabled' : '#000',
                    px: 1.75,
                    py: 1.5,
                    font: 'inherit',
                    lineHeight: 1.4,
                    overflowY: 'hidden',
                    '&:focus': {
                      outline: '2px solid rgba(8,242,40,0.45)',
                      borderColor: 'primary.main',
                    },
                  }}
                />
                <LoadingButton
                  aria-label="Send"
                  data-testid="conversation-send-button"
                  disabled={!trimmedDraft || composerDisabled}
                  loading={sendMessageMutation.isLoading}
                  onClick={() => {
                    handleSendMessage();
                  }}
                  sx={{
                    // REQ-006: the send control should stretch with the composer height instead of sitting shorter.
                    alignSelf: 'stretch',
                    flexShrink: 0,
                    minWidth: { xs: 48, sm: 128 },
                    width: { xs: 48, sm: 'auto' },
                    minHeight: 48,
                    height: 'auto',
                    borderRadius: { xs: 6, sm: 2 },
                    px: { xs: 0, sm: 2 },
                  }}
                  variant="contained"
                >
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                    Send
                  </Box>
                  <SendRoundedIcon sx={{ display: { xs: 'block', sm: 'none' } }} fontSize="small" />
                </LoadingButton>
              </Stack>
              {sendMessageMutation.isError ? (
                <Typography
                  color="error"
                  data-testid="conversation-send-error"
                  role="alert"
                  variant="body2"
                >
                  {sendMessageErrorMessage}
                </Typography>
              ) : null}
            </Stack>
          </Box>
        ) : null}
      </Paper>
    </Box>
  );
};

export default ReferralConversationDetail;
